import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { I18n, useWindowSize } from 'common'
import { Thumbnail } from '../../../../../../src/ui-components/products/Thumbnail'
import BaseDrawer from '../../../../../../src/ui-components/modals/BaseDrawer'
import styled from 'styled-components'
import { PriceBreak } from './PriceBreak'

import { create as createNiceModal, useModal } from '@ebay/nice-modal-react'
import { breakpoints } from '../../../../../../src/theme/breakpoints'
import { PriceBreakModel } from 'interfaces'

interface QuantityPriceBreaksProps {
  updatePriceBreaks?: (qty: number) => void
  priceBreaks: PriceBreakModel[]
  image?: string
  partNumber?: string
  description?: string
  lineCode?: string
}

export const QuantityPriceBreak = createNiceModal<QuantityPriceBreaksProps>(
  observer(
    ({
      updatePriceBreaks,
      priceBreaks,
      image,
      partNumber,
      description,
      lineCode,
    }): ReactElement => {
      const modal = useModal()
      const onClose = () => modal.remove()

      const windowWidth = useWindowSize()
      const isMobileView = Number(windowWidth.width) <= breakpoints.mobileLarge

      const headerContent = (
        <HeaderWrapper>
          {image && (
            <ImageContainer>
              <Thumbnail
                urlOrBase64={image}
                width={isMobileView ? 150 : 75}
                height={isMobileView ? 150 : 75}
                alt="brand"
              />
            </ImageContainer>
          )}
          <DetailsWrapper>
            <DescriptionName title={description}>{description}</DescriptionName>
            {(partNumber || lineCode) && (
              <MetaRow>
                {partNumber && (
                  <MetaItem>
                    <MetaLabel>Part&nbsp;#:&nbsp;</MetaLabel>
                    <MetaValue title={partNumber}>{partNumber}</MetaValue>
                  </MetaItem>
                )}
                {partNumber && lineCode && <MetaDivider>|</MetaDivider>}
                {lineCode && (
                  <MetaItem>
                    <MetaLabel>
                      <I18n name="lineCode" />
                      :&nbsp;
                    </MetaLabel>
                    <MetaValue title={lineCode}>{lineCode}</MetaValue>
                  </MetaItem>
                )}
              </MetaRow>
            )}
          </DetailsWrapper>
        </HeaderWrapper>
      )

      const bodyContent = priceBreaks && (
        <>
          <HeaderRow>
            <ColHeader width="33%" padding="0px 12px">
              <I18n name="minQty" />
            </ColHeader>
            <ColHeader width="34%" padding="0px 12px 0px 0px">
              <I18n name="pricePerItem" />
            </ColHeader>
            <ColHeader width="33%" />
          </HeaderRow>
          {priceBreaks?.map((priceBreak, index) => (
            <PriceBreak
              key={index}
              priceBreak={priceBreak}
              updatePriceBreaks={updatePriceBreaks}
              onClose={onClose}
            />
          ))}
        </>
      )

      return (
        <BaseDrawer
          open={modal.visible}
          title="quantityPriceBreak"
          header={headerContent}
          body={bodyContent}
          onClose={onClose}
          minWidth="533px"
          showHeaderDivider
        />
      )
    }
  )
)

const HeaderWrapper = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  margin-top: 16px;
`

const HeaderRow = styled.div`
  background-color: #000;
  color: #fff;
  height: 40px;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 16px;
`

interface HeaderRowProps {
  width?: string
  padding?: string
}
const ColHeader = styled.div<HeaderRowProps>`
  font-family: Oswald;
  font-weight: bold;
  align-items: center;
  font-size: 18px;
  line-height: 27px;
  width: ${(p) => p.width};
  color: #ffffff;
  padding: ${(p) => p.padding};
  text-align: right;
`

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  width: 100%;
  min-width: 0;
  line-height: 24px;
  padding-bottom: 24px;
`

const MetaItem = styled.span`
  display: inline-flex;
  align-items: baseline;
  min-width: 0;
`

const MetaDivider = styled.span`
  padding: 0 5px;
  flex-shrink: 0;
  font-family: Roboto;
  font-size: 16px;
  line-height: 24px;
  color: #050505;
  opacity: 0.9;
`

const DetailsWrapper = styled.div`
  flex: 1;
  min-width: 0;
`

const ImageContainer = styled.div`
  flex-shrink: 0;
  margin-right: 20px;
`

const DescriptionName = styled.span`
  display: block;
  margin-bottom: 7px;
  font-family: Oswald;
  font-weight: bold;
  font-size: 18px;
  line-height: 32px;
  color: #050505;
  mix-blend-mode: normal;
  opacity: 0.9;
  width: 100%;
  min-width: 0;
  word-break: break-word;
`

const MetaLabel = styled.span`
  flex-shrink: 0;
  font-family: Roboto;
  font-size: 16px;
  line-height: 24px;
  color: #050505;
  opacity: 0.9;
`

const MetaValue = styled.span`
  font-family: Roboto;
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  color: #050505;
  opacity: 0.9;
  min-width: 0;
  word-break: break-word;
`
